import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { v4 as uuid } from 'uuid';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import { arrayMove, useSortable, SortableContext } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Accordion,
  AccordionDetails,
  AccordionProps,
  AccordionSummary,
  Box,
  Button,
  IconButton,
  InputAdornment,
  MenuItem,
  OutlinedInput,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core';
import {
  Add,
  AddCircleOutline,
  Close,
  DragIndicator,
  ExpandMore,
  RemoveCircleOutline,
  TableChartOutlined
} from '@material-ui/icons';
import { useSort } from '@sierralabs/core-ui';
import {
  FormConfigRequired,
  FormConfigTitleDescription,
  FormElementPlugin,
  FormElementProps,
  useFormContext
} from '@sierralabs/forms-ui';

type ColumnType = 'text' | 'number';

const columnTypeOptions: { value: ColumnType; label: string }[] = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' }
];

const StyledContainer = styled.div`
  .config-title-container {
    display: flex;
    flex-direction: row;
  }
  .config-title-text {
    flex: 1;
  }
  .column-config {
    margin-left: -12px;
    margin-right: -12px;
  }
`;

export interface FormTableFieldColumn {
  // auto generated id, use id as column key
  id: string;
  name: string;
  type: ColumnType;
  inputAdornment?: string;
}

export interface FormTableFieldConfig {
  columns?: FormTableFieldColumn[];
}

export const FormConfigTableField = <FormValuesType extends object>() => {
  const { formState, setFormState, notifyFormEdit } = useFormContext<
    FormValuesType,
    FormTableFieldConfig
  >();
  const [expanded, setExpanded] = useState<string>();
  const [dragItem, setDragItem] = useState<FormTableFieldColumn>();
  const dragChangeRef = useRef({ sourceIndex: -1, targetIndex: -1 });
  const element = formState.selectedFormElement!;
  if (!element.config) element.config = {};
  const columns = element.config!.columns || [];
  const sort = useSort(columns, column => column.id);
  const sensors = useSensors(useSensor(PointerSensor));

  const clearDragChange = () => {
    dragChangeRef.current.sourceIndex = -1;
    dragChangeRef.current.targetIndex = -1;
  };

  return (
    <>
      <FormConfigTitleDescription />
      <FormConfigRequired />
      <StyledContainer>
        <Box className='config-title-container' my={1}>
          <Typography
            className='config-title-text'
            variant='overline'
            color='textSecondary'
          >
            Columns
          </Typography>
          <IconButton
            size='small'
            onClick={() => {
              const columnName = 'New Column';
              const item: FormTableFieldColumn = {
                id: uuid(),
                name: columnName,
                type: 'text'
              };
              columns.push(item);
              element.config!.columns = columns;
              setFormState({ ...formState, selectedFormElement: element });
              notifyFormEdit();
            }}
          >
            <AddCircleOutline />
          </IconButton>
        </Box>
        <DndContext
          sensors={sensors}
          onDragStart={event => {
            // prevent dragging if currently dragging an event
            if (dragItem) {
              return;
            }
            const index = columns.findIndex(
              column => column.id === event.active.id
            );
            const column = columns[index];
            dragChangeRef.current.sourceIndex = index;
            setDragItem(column);
          }}
          onDragOver={event => {
            const { active, over } = event;
            if (over && active.id !== over.id) {
              dragChangeRef.current.targetIndex = sort.findIndex(over.id);
              sort.reorder(active.id, over.id);
            }
          }}
          onDragEnd={() => {
            const { sourceIndex, targetIndex } = dragChangeRef.current;
            // dropped outside the list
            if (
              sourceIndex > -1 &&
              targetIndex > -1 &&
              sourceIndex !== targetIndex
            ) {
              const values = arrayMove(columns, sourceIndex, targetIndex);
              element.config!.columns = values;
              setFormState({ ...formState, selectedFormElement: element });
              notifyFormEdit();
            }
            clearDragChange();
            setDragItem(undefined);
          }}
          onDragCancel={() => {
            clearDragChange();
            setDragItem(undefined);
          }}
        >
          <SortableContext items={sort.items.map(column => column.id)}>
            <div className='column-config'>
              {sort.items.map((column, index) => (
                <SortableFormTableFieldColumnConfig
                  key={column.id}
                  value={column}
                  expanded={column.id === expanded}
                  setExpanded={expanded => {
                    setExpanded(expanded ? column.id : undefined);
                  }}
                  onChange={(property, value) => {
                    column[property] = value;
                    setFormState({
                      ...formState,
                      selectedFormElement: element
                    });
                    notifyFormEdit();
                  }}
                  onDelete={() => {
                    element.config!.columns?.splice(index, 1);
                    setFormState({
                      ...formState,
                      selectedFormElement: element
                    });
                    notifyFormEdit();
                  }}
                />
              ))}
            </div>
          </SortableContext>
          <DragOverlay>
            {dragItem && (
              <FormTableFieldColumnConfig isDragging value={dragItem} />
            )}
          </DragOverlay>
        </DndContext>
      </StyledContainer>
    </>
  );
};

export default FormConfigTableField;

const StyledAccordion = styled(Accordion)`
  &.dragging {
    opacity: 0.75;
  }
  .MuiAccordionSummary-root {
    padding-left: 5px;
    &.Mui-expanded {
      min-height: unset;
    }
    .MuiAccordionSummary-content {
      margin: 12px 0px;
      align-items: center;
      .delete-button {
        margin-right: 7px;
      }
    }
  }
  .drag-handle {
    display: flex;
  }
  .form-fields {
    display: flex;
    flex-direction: column;
    width: 100%;
    .field {
      margin-top: 10px;
      margin-bottom: 10px;
    }
  }
`;

export interface FormTableFieldColumnConfigProps {
  value: FormTableFieldColumn;
  expanded?: boolean;
  isDragging?: boolean;
  accordionProps?: Omit<AccordionProps, 'children'>;
  dragHandleProps?: React.HTMLProps<HTMLDivElement>;
  setExpanded?: (expanded: boolean) => void;
  onChange?: <Property extends keyof FormTableFieldColumn>(
    property: Property,
    value: FormTableFieldColumn[Property]
  ) => void;
  onDelete?: () => void;
}

export const FormTableFieldColumnConfig: React.FC<
  FormTableFieldColumnConfigProps
> = (props: FormTableFieldColumnConfigProps) => {
  const {
    value,
    expanded = false,
    isDragging = false,
    accordionProps,
    dragHandleProps,
    setExpanded,
    onChange,
    onDelete
  } = props;
  const [column, setColumn] = useState<Partial<FormTableFieldColumn>>(value);
  const classes: string[] = [];
  accordionProps?.className && classes.push(accordionProps.className);
  isDragging && classes.push('dragging');

  useEffect(() => {
    setColumn(value);
  }, [value]);

  return (
    <StyledAccordion
      {...accordionProps}
      expanded={expanded}
      className={classes.join(' ')}
      onChange={(_, expanded) => setExpanded?.(expanded)}
    >
      <AccordionSummary expandIcon={<ExpandMore />}>
        <div className='drag-handle' {...dragHandleProps}>
          <DragIndicator color='disabled' />
        </div>
        <IconButton size='small' className='delete-button' onClick={onDelete}>
          <RemoveCircleOutline />
        </IconButton>
        {column.name}
      </AccordionSummary>
      <AccordionDetails>
        <div className='form-fields'>
          <TextField
            fullWidth
            size='small'
            variant='outlined'
            label='Column Name'
            className='field'
            value={column.name || ''}
            onChange={event => {
              const { value: name } = event.target;
              setColumn({ ...column, name });
              onChange?.('name', name);
            }}
          />
          <TextField
            select
            fullWidth
            size='small'
            variant='outlined'
            label='Column Type'
            className='field'
            value={column.type || ''}
            onChange={event => {
              const type = event.target.value as ColumnType;
              setColumn({ ...column, type });
              onChange?.('type', type);
            }}
          >
            {columnTypeOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            size='small'
            variant='outlined'
            label='Input Adornment'
            className='field'
            value={column.inputAdornment || ''}
            onChange={event => {
              const { value: inputAdornment } = event.target;
              setColumn({ ...column, inputAdornment });
              onChange?.('inputAdornment', inputAdornment);
            }}
          />
        </div>
      </AccordionDetails>
    </StyledAccordion>
  );
};

export const SortableFormTableFieldColumnConfig: React.FC<
  FormTableFieldColumnConfigProps
> = props => {
  const { value: column } = props;
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    transform,
    transition
  } = useSortable({ id: column.id || '' });
  return (
    <FormTableFieldColumnConfig
      {...props}
      isDragging={isDragging}
      dragHandleProps={{ ...attributes, ...listeners }}
      accordionProps={{
        innerRef: setNodeRef,
        style: {
          opacity: isDragging ? 0.75 : undefined,
          transform: CSS.Translate.toString(transform),
          transition: transition || undefined
        }
      }}
    />
  );
};

// FormTableField

const StyledTableContainer = styled(TableContainer)`
  padding: 16px;
  border-radius: 4px;
  border: 1px solid #ccc;
`;

export type FormTableFieldProps<T> = FormElementProps<
  T,
  FormTableFieldConfig
> & { value?: Record<string, any>[] };

export const FormTableField = <T extends Record<string, any>>({
  element,
  edit,
  disabled,
  ...props
}: FormTableFieldProps<T>) => {
  const { formValues, setFormValue } = useFormContext<
    T,
    FormTableFieldConfig
  >();
  const key = (element.dataSource as keyof T) || element.key;
  const { columns = [] } = element.config || {};
  const values =
    props.value || (formValues[key] as Record<string, any>[]) || [];

  const createInitialItem = () => {
    const item: Record<string, any> = {};
    for (const column of columns) {
      item[column.id] = column.type === 'text' ? '' : 0;
    }
    return item;
  };

  return (
    <StyledTableContainer>
      <Table>
        <TableHead>
          <TableRow>
            {columns.map(column => (
              <TableCell key={column.id}>{column.name}</TableCell>
            ))}
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {values.map((value, index) => (
            <TableRow key={index}>
              {columns.map(column => (
                <TableCell key={column.id}>
                  <OutlinedInput
                    type={column.type}
                    disabled={edit || disabled}
                    value={value[column.id]}
                    onChange={event => {
                      const newValues = [...values];
                      newValues[index] = {
                        ...values[index],
                        [column.id]: event.target.value
                      };
                      setFormValue(key, newValues as any);
                    }}
                    startAdornment={
                      column.inputAdornment ? (
                        <InputAdornment position='start'>
                          {column.inputAdornment}
                        </InputAdornment>
                      ) : undefined
                    }
                  />
                </TableCell>
              ))}
              <TableCell>
                <IconButton
                  size='small'
                  aria-label='delete'
                  disabled={edit || disabled}
                  onClick={() => {
                    values.splice(index, 1);
                    setFormValue(key, values as any);
                  }}
                >
                  <Close />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Box m={2} mb={0}>
        <Button
          variant='outlined'
          startIcon={<Add />}
          disabled={edit || disabled}
          onClick={() => {
            const updatedValues = [...values, createInitialItem()];
            setFormValue(key, updatedValues as any);
          }}
        >
          Add Item
        </Button>
      </Box>
    </StyledTableContainer>
  );
};

// FormTableFieldPlugin

export const FormTableFieldPlugin: FormElementPlugin<
  any,
  FormTableFieldConfig
> = {
  icon: <TableChartOutlined />,
  name: 'Table Field',
  type: 'TableField',
  dataTypes: ['jsonb'],
  configurator: FormConfigTableField,
  element: FormTableField as React.FC<
    FormElementProps<any, FormTableFieldConfig>
  >
};
